import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { concatAndDeDuplicateObjects } from '@/lib/utils';

interface CheckinItem {
  id?: string;
  booking_id: string;
  booking_date: string;
  [key: string]: any;
}

interface CheckinsState {
  items: CheckinItem[];
  loading: boolean;
  error: string | null;
}

const initialState: CheckinsState = {
  items: [],
  loading: false,
  error: null,
};

const checkinsSlice = createSlice({
  name: 'checkins',
  initialState,
  reducers: {
    // Action creators for Redux Offline
    checkIn: (state, action: PayloadAction<CheckinItem>) => {
      state.items = [action.payload, ...state.items];
    },
    checkInCommit: (state, action: PayloadAction<any>) => {
      if (!action.payload) return;
      const serverCheckin = action.payload;
      const index = state.items.findIndex(
        item =>
          item.booking_id === serverCheckin.booking_id &&
          item.booking_date === serverCheckin.booking_date
      );
      if (index !== -1) {
        // Normal case: update optimistic item with server data
        state.items[index] = { ...state.items[index], ...serverCheckin };
      } else {
        // Race condition: optimistic item was wiped by GET_BOOKINGS_COMMIT — restore from server
        state.items = [serverCheckin, ...state.items];
      }
    },
    checkInFailed: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    checkOut: (state, action: PayloadAction<CheckinItem>) => {
      const index = state.items.findIndex(
        (item) => 
          action.payload.booking_id === item.booking_id && 
          action.payload.booking_date === item.booking_date
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    checkOutCommit: (state, action: PayloadAction<any>) => {
      if (!action.payload) return;
      const serverCheckin = action.payload;
      const index = state.items.findIndex(
        item =>
          item.booking_id === serverCheckin.booking_id &&
          item.booking_date === serverCheckin.booking_date
      );
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...serverCheckin };
      }
    },
    checkOutFailed: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    updateCheckinField: (state, action: PayloadAction<{
      booking_id: string;
      booking_date: string;
      key: string;
      value: any;
    }>) => {
      const index = state.items.findIndex(
        (item) => 
          action.payload.booking_id === item.booking_id && 
          action.payload.booking_date === item.booking_date
      );
      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          [action.payload.key]: action.payload.value
        };
      }
    },
    updateCheckinFieldCommit: (state, action: PayloadAction<any>) => {
      if (!action.payload) return;
      const serverCheckin = action.payload;
      const index = state.items.findIndex(
        item =>
          item.booking_id === serverCheckin.booking_id &&
          item.booking_date === serverCheckin.booking_date
      );
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...serverCheckin };
      }
    },
    updateCheckinFieldFailed: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

  },
  extraReducers: (builder) => {
    builder.addCase('GET_BOOKINGS_COMMIT', (state, action: PayloadAction<{
      checkins?: CheckinItem[];
      page?: number;
    }>) => {
      if (action.payload.page) {
        if (action.payload.checkins && action.payload.checkins.length > 0) {
          state.items = [...state.items, ...action.payload.checkins];
        }
      } else {
        state.items = action.payload.checkins || [];
      }
    });
  },
});

export const {
  checkIn,
  checkInCommit,
  checkInFailed,
  checkOut,
  checkOutCommit,
  checkOutFailed,
  updateCheckinField,
  updateCheckinFieldCommit,
  updateCheckinFieldFailed,
} = checkinsSlice.actions;

export default checkinsSlice.reducer;

// Selectors
export const selectCheckinsForBookings = (state: any, bookings: any[]) => {
  return state.checkins.items.filter((checkin: CheckinItem) => 
    bookings.find((booking) => 
      booking.id === checkin.booking_id && 
      booking.date === checkin.booking_date
    )
  );
};